import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Checkbox, Input, Modal, Slider, Tooltip, message } from 'antd'
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import {
  getActiveAutoProcessTemplate,
  pixelsUrl,
  shadowEditorSave,
  syncStageToActiveTemplate,
  type AssetChainOut,
  type BackgroundEditorParams,
  type ShadowEditorParams,
} from '../../api/client'
import { RATIO_PRESETS, BG_PRESET_COLOR, inferredDefaultBackgroundParams, loadHtmlImage, migrateBackgroundGradients } from './backgroundPresets'
import { getLastPixelLoadTiming, logEditorLoadTiming } from './mediaLoader'
import { MODAL_SURFACE_CLASS, useModalScrollLock } from '../../saas/utils/scrollLock'
import EditorHeader, { confirmDiscardAndClose } from './EditorHeader'
import { submitEditorSave } from './editorSaveManager'
import {
  DEFAULT_SHADOW_PARAMS,
  analyzeSubject,
  clamp,
  lightVector,
  migrateShadowParams,
  renderComposite,
  type CompositionParams,
  type ShadowParams,
  type SubjectAnalysis,
} from './shadowRender'
import {
  deletePresetFromLibrary,
  findPresetIdByName,
  loadPresetLibrary,
  makePresetThumbnail,
  nextDefaultPresetName,
  savePresetToLibrary,
  type SavedShadowPreset,
} from './shadowPresetLibrary'

/** True when `shadow`'s actual rendering params still exactly match the
 * preset it's linked to — used to show "预设2" vs "预设2 · 已调整" (spec
 * 二.6) without ever mutating the preset itself. Deliberately ignores the
 * bookkeeping-only `sourcePresetId`/`legacyPresetName` fields. */
function shadowParamsEqual(a: ShadowParams, b: ShadowParams): boolean {
  const strip = (s: ShadowParams) => {
    const { sourcePresetId: _s, legacyPresetName: _l, ...rest } = s
    return rest
  }
  return JSON.stringify(strip(a)) === JSON.stringify(strip(b))
}

/**
 * Shadow Editor (灯光与落地阴影编辑器) — the last stage of the pipeline
 * (色底图 → 阴影图). Composition (canvas ratio / background / subject
 * placement) is INHERITED read-only from the Background Editor's saved
 * state; this editor only ever touches lighting/shadow parameters. See
 * `shadowRender.ts` for the ground-projection algorithm.
 */

interface Props {
  /** Required in asset mode; in template mode pass the template id so the
   * open/reload effect keys correctly per template. */
  chainId: string
  filename: string
  transparentUrl: string
  backgroundParams: BackgroundEditorParams | null
  initialShadowParams?: ShadowEditorParams | null
  open: boolean
  onClose: () => void
  /** Asset mode (default). Ignored when `onSaveOverride` is provided. */
  onSaved?: (chain: AssetChainOut) => void
  /** Template mode: hands the exported canvas blob + params to the caller
   * instead of posting to `/chains/:id/editor/shadow/save` — same render
   * code either way (spec 十: 同一个 ShadowEditor). */
  onSaveOverride?: (blob: Blob, params: ShadowEditorParams) => Promise<void>
  titlePrefix?: string
}

const PREVIEW_LONG_SIDE = 1100

function ratioSize(ratioKey: string) {
  return RATIO_PRESETS.find((r) => r.key === ratioKey) || RATIO_PRESETS[0]
}

function previewSize(w: number, h: number) {
  const s = Math.min(1, PREVIEW_LONG_SIDE / Math.max(w, h))
  return { w: Math.max(1, Math.round(w * s)), h: Math.max(1, Math.round(h * s)) }
}

function toComposition(bg: BackgroundEditorParams): CompositionParams {
  const ratio = ratioSize(bg.ratioKey)
  const bgColor =
    bg.bgPreset === 'custom' ? bg.bgCustomColor : BG_PRESET_COLOR[bg.bgPreset as keyof typeof BG_PRESET_COLOR] || '#ffffff'
  return {
    canvasW: ratio.w,
    canvasH: ratio.h,
    bgColor,
    // `bg` is the raw persisted Background Editor state, which may still be
    // in a legacy (pre-composable) scheme — the inherited-background
    // preview here must resolve to the exact same combined gradients the
    // Background Editor itself would show for the same params (spec 九),
    // never the raw un-migrated fields.
    gradients: migrateBackgroundGradients(bg),
    scalePct: bg.transform.scalePct,
    offsetXFrac: bg.transform.offsetXFrac,
    offsetYFrac: bg.transform.offsetYFrac,
  }
}

const DIAL_AZIMUTHS = [0, 45, 90, 135, 180, 225, 270, 315] as const

const DIAL_LABEL_KEY: Record<number, string> = {
  0: 'front',
  45: 'frontRight',
  90: 'right',
  135: 'backRight',
  180: 'back',
  225: 'backLeft',
  270: 'left',
  315: 'frontLeft',
}

const DIAL_RADIUS = 58

function dialPos(azimuthDeg: number, radius: number) {
  const v = lightVector(azimuthDeg)
  return { x: v.x * radius, y: v.y * radius }
}

export default function ShadowEditorModal({
  chainId,
  filename,
  transparentUrl,
  backgroundParams,
  initialShadowParams,
  open,
  onClose,
  onSaved,
  onSaveOverride,
  titlePrefix,
}: Props) {
  const { t } = useTranslation(['autoProcess', 'actions'])
  useModalScrollLock(open)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const analysisRef = useRef<SubjectAnalysis | null>(null)
  const rafRef = useRef<number | null>(null)

  const [imgLoaded, setImgLoaded] = useState(false)
  const [loadError, setLoadError] = useState('')
  // Editor Load Performance P0 (2026-08) — see the identical pattern (and
  // rationale) in `BackgroundEditorModal`: `imgRef`/`analysisRef` may hold
  // the fast `transparent_proxy` preview (asset mode only) until the
  // full-resolution `transparent` source finishes loading in the
  // background; `doSave` awaits `fullResPromiseRef` before exporting.
  const fullResReadyRef = useRef(false)
  const fullResPromiseRef = useRef<Promise<HTMLImageElement> | null>(null)
  const loadStartRef = useRef(0)
  const [fullResVersion, setFullResVersion] = useState(0)

  const effectiveBg = useMemo<BackgroundEditorParams>(
    () => backgroundParams || inferredDefaultBackgroundParams(),
    [backgroundParams],
  )
  const composition = useMemo(() => toComposition(effectiveBg), [effectiveBg])
  const usedFallback = !backgroundParams

  const savedInitialRef = useRef<ShadowParams>(DEFAULT_SHADOW_PARAMS)
  const [shadow, setShadow] = useState<ShadowParams>(DEFAULT_SHADOW_PARAMS)
  const [dirty, setDirty] = useState(false)
  const [saveSubmitted, setSaveSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)
  // 高级调整默认展开（VISTOERA_SHADOW_EDITOR_SINGLE_LIGHT_V2 spec 十七）——
  // 进入编辑器后直接可见阴影强度/远端渐隐/投影收窄程度等参数，不再需要先点
  // "展开"；仍保留可收起。
  const [advancedOpen, setAdvancedOpen] = useState(true)

  const [presets, setPresets] = useState<SavedShadowPreset[]>([])
  const [activePresetId, setActivePresetId] = useState<string | null>(null)
  // "另存为新预设" (spec 二.4) is a deliberately SEPARATE action from the
  // main "保存" button — it only ever writes to the local preset library,
  // never to the chain/template, and is the ONLY code path allowed to call
  // `savePresetToLibrary` (spec 二: 普通保存绝对不能隐式新建预设).
  const [saveAsPresetOpen, setSaveAsPresetOpen] = useState(false)
  const [saveAsPresetName, setSaveAsPresetName] = useState('')
  // "保存" vs "保存并同时更新模板" (spec 十) — only relevant in asset mode;
  // irrelevant/hidden in template mode (onSaveOverride).
  const [activeTemplateName, setActiveTemplateName] = useState<string | null>(null)
  const [alsoUpdateTemplate, setAlsoUpdateTemplate] = useState(false)

  const dialRef = useRef<HTMLDivElement>(null)
  const draggingDial = useRef(false)

  useEffect(() => {
    if (!open) return
    const start = migrateShadowParams(initialShadowParams)
    savedInitialRef.current = start
    setShadow(start)
    setDirty(false)
    setAdvancedOpen(true)
    const library = loadPresetLibrary()
    setPresets(library)
    // Resolve the source preset link on open: prefer the stable id; fall
    // back to a one-time name-based lookup for pre-`sourcePresetId` data
    // (spec 二.7/十) — never re-derived on every render/tweak afterwards.
    setActivePresetId(start.sourcePresetId || findPresetIdByName(library, start.legacyPresetName))
    setImgLoaded(false)
    setLoadError('')
    imgRef.current = null
    analysisRef.current = null
    fullResReadyRef.current = false
    fullResPromiseRef.current = null
    loadStartRef.current = performance.now()
    setAlsoUpdateTemplate(false)

    if (onSaveOverride) {
      // Template mode: `transparentUrl` is already a same-origin-to-backend
      // FileResponse (`serve_preview_file`), never R2-redirected, and there
      // is no `transparent_proxy` derivative for a template — load it
      // directly, exactly as before. Full-res by construction.
      loadHtmlImage(transparentUrl)
        .then((img) => {
          imgRef.current = img
          analysisRef.current = analyzeSubject(img)
          fullResReadyRef.current = true
          setImgLoaded(true)
        })
        .catch(() => setLoadError(t('autoProcess:shadowEditor.loadFailed')))
      return
    }

    // Asset mode: fast `transparent_proxy` preview first (Editor Load
    // Performance P0, 2026-08), then the full-resolution `transparent`
    // loads in the background and silently upgrades `imgRef`/`analysisRef`
    // (re-scheduling a redraw) once ready. `doSave` always awaits the
    // full-res promise before exporting.
    const previewUrl = pixelsUrl(chainId, 'transparent_proxy')
    const fullResUrl = pixelsUrl(chainId, 'transparent')
    let cancelled = false
    // Preview Regression Forensic P0 (2026-08-29): the fast `transparent_
    // proxy` preview and the authoritative full-resolution `transparent`
    // load race independently — confirmed live on Preview that the proxy
    // derivative's on-demand cold-generation can fail (backend contention
    // on a never-before-proxied real photo) while the full-res source
    // loads fine seconds later. The old code set a *permanent* `loadError`
    // straight from the preview's own `.catch()`, so the editor stayed
    // stuck on "透明底图加载失败" forever even once the full-res promise
    // below went on to succeed with the exact same pixels. Only declare a
    // real failure once BOTH loads are known to have failed.
    let previewFailed = false
    let fullResFailed = false
    const maybeReportFailure = () => {
      if (!cancelled && previewFailed && fullResFailed) setLoadError(t('autoProcess:shadowEditor.loadFailed'))
    }

    loadHtmlImage(previewUrl)
      .then((img) => {
        if (cancelled) return
        const tDrawStart = performance.now()
        imgRef.current = img
        analysisRef.current = analyzeSubject(img)
        setImgLoaded(true)
        const timing = getLastPixelLoadTiming(previewUrl)
        logEditorLoadTiming({
          asset_id: chainId,
          editor_type: 'shadow',
          source_kind: 'transparent_proxy',
          source_url_type: 'same-origin-pixels-proxy',
          source_width: img.width,
          source_height: img.height,
          source_bytes: timing?.bytes ?? null,
          metadata_ms: null,
          image_ttfb_ms: timing?.ttfbMs ?? null,
          image_download_ms: timing?.downloadMs ?? null,
          decode_ms: timing?.decodeMs ?? null,
          canvas_init_ms: null,
          first_draw_ms: performance.now() - tDrawStart,
          total_ms: performance.now() - loadStartRef.current,
          deduped_request: timing?.deduped ?? false,
        })
      })
      .catch(() => {
        previewFailed = true
        maybeReportFailure()
      })

    const fullResPromise = loadHtmlImage(fullResUrl).then((img) => {
      if (!cancelled) {
        imgRef.current = img
        analysisRef.current = analyzeSubject(img)
        fullResReadyRef.current = true
        setFullResVersion((v) => v + 1)
        // Self-heal: the full-res source is authoritative and just proved
        // the real pixels ARE available, even if the fast preview above
        // had already failed — never leave the editor stuck on a stale
        // error once the actual image is in hand.
        setLoadError('')
        setImgLoaded(true)
      }
      return img
    })
    fullResPromiseRef.current = fullResPromise
    fullResPromise.catch(() => {
      fullResFailed = true
      maybeReportFailure()
    })

    getActiveAutoProcessTemplate()
      .then((r) => setActiveTemplateName(r.data.name))
      .catch(() => setActiveTemplateName(null))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, chainId, transparentUrl])

  const scheduleDraw = useCallback(() => {
    if (rafRef.current != null) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null
      const canvas = canvasRef.current
      const analysis = analysisRef.current
      const img = imgRef.current
      if (!canvas || !analysis || !img) return
      const { w, h } = previewSize(composition.canvasW, composition.canvasH)
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w
        canvas.height = h
      }
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      renderComposite(ctx, w, h, img, analysis, composition, shadow)
    })
  }, [composition, shadow])

  useEffect(() => {
    scheduleDraw()
  }, [scheduleDraw, imgLoaded, fullResVersion])

  useEffect(() => {
    return () => {
      // StrictMode dev double-invokes effects (mount -> cleanup -> mount) so this
      // cleanup can run while the component stays mounted; resetting the ref (not
      // just cancelling the frame) is required so the next scheduleDraw() call
      // isn't permanently blocked by a stale non-null rafRef.
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [])

  // Deliberately does NOT clear `activePresetId` — tweaking a value after
  // picking a preset still means "this template's shadow stage is based on
  // preset X, now adjusted" (spec 二.5/6: 保存后仍应记录来源预设并显示"已
  // 调整"), not "no preset at all". Only `handleRestoreDefault` (which
  // isn't derived from any preset) clears it.
  const updateShadow = useCallback((updater: (s: ShadowParams) => ShadowParams) => {
    setShadow((s) => updater(s))
    setDirty(true)
  }, [])

  const setField = useCallback(
    (patch: Partial<ShadowParams['shadow']>) => {
      updateShadow((s) => ({ ...s, shadow: { ...s.shadow, ...patch } }))
    },
    [updateShadow],
  )

  const handleApplyPreset = (preset: SavedShadowPreset) => {
    setShadow(JSON.parse(JSON.stringify(preset.params)))
    setDirty(true)
    setActivePresetId(preset.id)
  }

  const handleDeletePreset = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setPresets(deletePresetFromLibrary(id))
    if (activePresetId === id) setActivePresetId(null)
  }

  /** Dial owns azimuth ONLY — never writes heightPct (spec: 圆盘与高度彻底解耦). */
  const handleAzimuthOnly = (azimuthDeg: number) => {
    updateShadow((s) => ({ ...s, light: { ...s.light, azimuthDeg: ((azimuthDeg % 360) + 360) % 360 } }))
  }

  /** Height slider owns elevation ONLY — never writes azimuthDeg. */
  const handleHeightOnly = (heightPct: number) => {
    updateShadow((s) => ({ ...s, light: { ...s.light, heightPct: clamp(heightPct, 0, 100) } }))
  }

  const onDialPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Capture on the dial itself (never on a snap-point button). Capturing
    // on e.target used to bind moves to the fixed-point <button>, so once
    // the pointer left that 14px hit target the dial stopped receiving
    // pointermove — which made free dragging feel like "only 8 snaps work".
    draggingDial.current = true
    dialRef.current?.setPointerCapture(e.pointerId)
    updateAzimuthFromPointer(e.clientX, e.clientY)
  }
  const onDialPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingDial.current) return
    updateAzimuthFromPointer(e.clientX, e.clientY)
  }
  const updateAzimuthFromPointer = (clientX: number, clientY: number) => {
    const dial = dialRef.current
    if (!dial) return
    const rect = dial.getBoundingClientRect()
    const dx = clientX - (rect.left + rect.width / 2)
    const dy = clientY - (rect.top + rect.height / 2)
    // Radius ignored on purpose — elevation lives only on the height slider.
    if (dx * dx + dy * dy < 0.25) return
    handleAzimuthOnly((Math.atan2(dx, dy) * 180) / Math.PI)
  }
  const onDialPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    draggingDial.current = false
    try {
      dialRef.current?.releasePointerCapture(e.pointerId)
    } catch {
      /* already released */
    }
  }

  const handleReRecognizeContact = () => {
    updateShadow((s) => ({ ...s, groundBaselineOffsetFrac: 0 }))
    message.success(t('autoProcess:shadowEditor.baselineRedetected'))
  }

  const handleRestoreDefault = () => {
    setActivePresetId(null)
    updateShadow(() => JSON.parse(JSON.stringify(DEFAULT_SHADOW_PARAMS)))
  }

  const handleRevertToSaved = () => {
    const saved = savedInitialRef.current
    setShadow(JSON.parse(JSON.stringify(saved)))
    setActivePresetId(saved.sourcePresetId || null)
    setDirty(false)
  }

  const requestClose = () => confirmDiscardAndClose(dirty && !saveSubmitted, onClose, t('autoProcess:editorHeader.discardConfirm'))

  const activePreset = useMemo(() => presets.find((p) => p.id === activePresetId) || null, [presets, activePresetId])
  const isAdjustedFromPreset = activePreset ? !shadowParamsEqual(shadow, activePreset.params) : false

  const openSaveAsPresetDialog = () => {
    setSaveAsPresetName(nextDefaultPresetName(presets, (n) => t('cutoutEditor.defaultPresetName', { n })))
    setSaveAsPresetOpen(true)
  }

  /** ONLY code path allowed to call `savePresetToLibrary` — a deliberate,
   * separate action from "保存" (spec 二.4: 只有明确点击"新建预设"才创建
   * 预设). Never touches the chain/template; the user still has to click
   * the main "保存" button afterwards to persist the new `sourcePresetId`
   * link there. */
  const handleSaveAsNewPreset = () => {
    const canvas = canvasRef.current
    const finalName = saveAsPresetName.trim() || nextDefaultPresetName(presets, (n) => t('cutoutEditor.defaultPresetName', { n }))
    // A preset's own stored snapshot never carries a `sourcePresetId` back-
    // reference — that field only means something on a chain/template's
    // *usage* of a preset, not on the preset itself.
    const { legacyPresetName: _l, sourcePresetId: _s, ...toStore } = shadow
    const thumb = canvas ? makePresetThumbnail(canvas) : ''
    const updated = savePresetToLibrary(finalName, toStore, thumb)
    setPresets(updated)
    const created = updated.find((p) => p.name === finalName) || updated[0]
    setActivePresetId(created?.id || null)
    setDirty(true)
    setSaveAsPresetOpen(false)
    message.success(t('autoProcess:shadowEditor.presetCreated', { name: finalName }))
  }

  const doSave = async () => {
    let analysis = analysisRef.current
    let img = imgRef.current
    if (!analysis || !img || saving || saveSubmitted) return
    setSaving(true)
    try {
      // Preview optimization must never lower the exported file's quality
      // (Editor Load Performance P0, 2026-08) — if the full-resolution
      // source is still loading in the background, wait for it before
      // exporting, rather than shipping today's fast `transparent_proxy`-
      // resolution frame.
      if (!fullResReadyRef.current && fullResPromiseRef.current) {
        await fullResPromiseRef.current
        analysis = analysisRef.current
        img = imgRef.current
        if (!analysis || !img) throw new Error(t('autoProcess:shadowEditor.highResLoadFailed'))
      }
      // A normal save NEVER creates/renames a preset — it only ever writes
      // the current params + a reference to whichever preset (if any) is
      // currently active (spec 二.1/2/3).
      const { legacyPresetName: _l, ...rest } = shadow
      const savedShadow: ShadowParams = { ...rest, sourcePresetId: activePresetId || null }

      const ratio = ratioSize(effectiveBg.ratioKey)
      const outCanvas = document.createElement('canvas')
      outCanvas.width = ratio.w
      outCanvas.height = ratio.h
      const ctx = outCanvas.getContext('2d')
      if (!ctx) throw new Error(t('autoProcess:shadowEditor.exportCanvasFailed'))
      renderComposite(ctx, ratio.w, ratio.h, img, analysis, composition, savedShadow)
      const blob = await new Promise<Blob | null>((resolve) => outCanvas.toBlob(resolve, 'image/jpeg', 0.95))
      if (!blob) throw new Error(t('autoProcess:shadowEditor.exportFailed'))
      if (onSaveOverride) {
        await onSaveOverride(blob, savedShadow as unknown as ShadowEditorParams)
        savedInitialRef.current = savedShadow
        setShadow(savedShadow)
        setDirty(false)
        message.success(t('autoProcess:shadowEditor.savedToTemplate'))
        onClose()
      } else {
        // Hand upload+persist to the module-level save manager so the task
        // survives this editor closing immediately (Async Save P0).
        const accepted = submitEditorSave({
          kind: 'shadow',
          chainId,
          run: async () => {
            const { data } = await shadowEditorSave(chainId, blob, savedShadow as unknown as ShadowEditorParams)
            return data
          },
          onSaved: (data) => onSaved?.(data),
          successMessage: alsoUpdateTemplate
            ? t('autoProcess:shadowEditor.savedAndSynced')
            : t('autoProcess:shadowEditor.saved'),
        })
        if (accepted) {
          savedInitialRef.current = savedShadow
          setShadow(savedShadow)
          setSaveSubmitted(true)
          setDirty(false)
          if (alsoUpdateTemplate) {
            syncStageToActiveTemplate('shadow', savedShadow as unknown as Record<string, unknown>).catch(() => undefined)
          }
          onClose()
        }
      }
    } catch (e) {
      // Keep the current edited params/canvas exactly as-is on failure —
      // never drop the user's work (spec 十七).
      message.error((e as Error).message || t('autoProcess:shadowEditor.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  // Handle always sits on a fixed outer ring. Its radius must NOT depend on
  // heightPct — otherwise dragging the height slider would visually move the
  // light point (the other half of the coupling bug).
  const handlePos = dialPos(shadow.light.azimuthDeg, DIAL_RADIUS)

  return (
    <Modal
      open={open}
      onCancel={requestClose}
      footer={null}
      closable={false}
      width="90vw"
      centered
      style={{ paddingBottom: 0 }}
      styles={{ body: { height: '88vh', padding: 0 }, mask: { backgroundColor: 'transparent' } }}
      wrapClassName={MODAL_SURFACE_CLASS}
      destroyOnClose
    >
      <div className="shdedit-shell" data-testid="shadow-editor-modal">
        <EditorHeader
          testIdPrefix="shdedit"
          title={`${titlePrefix || t('autoProcess:shadowEditor.title')} · ${filename}`}
          dirty={dirty && !saveSubmitted}
          saving={saving || saveSubmitted}
          saveDisabled={!imgLoaded}
          onCancel={handleRevertToSaved}
          onReset={handleRestoreDefault}
          onSave={doSave}
          onClose={requestClose}
          extra={
            !onSaveOverride && activeTemplateName ? (
              <Tooltip
                title={t('autoProcess:shadowEditor.syncToTemplateTooltip', { name: activeTemplateName })}
              >
                <Checkbox
                  className="edhdr-sync-template-check"
                  checked={alsoUpdateTemplate}
                  onChange={(e) => setAlsoUpdateTemplate(e.target.checked)}
                  data-testid="shdedit-save-sync-checkbox"
                >
                  {t('autoProcess:shadowEditor.syncToTemplate')}
                </Checkbox>
              </Tooltip>
            ) : undefined
          }
        />

        {usedFallback && (
          <div className="shdedit-fallback-banner" data-testid="shdedit-fallback-banner">
            {t('autoProcess:shadowEditor.missingBackgroundParams')}
          </div>
        )}

        <div className="shdedit-body">
          <aside className="shdedit-panel">
            <div className="shdedit-panel-scroll">
              <ShdEditGroup title={t('autoProcess:shadowEditor.presets')}>
                {presets.length === 0 ? (
                  <div className="shdedit-hint" data-testid="shdedit-preset-empty">
                    {t('autoProcess:shadowEditor.noPresetsHint')}
                  </div>
                ) : (
                  <div className="shdedit-preset-grid" data-testid="shdedit-preset-list">
                    {presets.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        data-testid={`shdedit-preset-card-${p.id}`}
                        className={`shdedit-preset-card${activePresetId === p.id ? ' active' : ''}`}
                        onClick={() => handleApplyPreset(p)}
                        title={p.name}
                      >
                        <img src={p.thumbnail} alt={p.name} className="shdedit-preset-thumb" />
                        <span className="shdedit-preset-name">{p.name}</span>
                        <span
                          role="button"
                          tabIndex={0}
                          className="shdedit-preset-delete"
                          data-testid={`shdedit-preset-delete-${p.id}`}
                          onClick={(e) => handleDeletePreset(p.id, e)}
                          aria-label={t('autoProcess:shadowEditor.deletePresetAria', { name: p.name })}
                        >
                          <DeleteOutlined />
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {activePreset && (
                  <div className="shdedit-preset-source-tag" data-testid="shdedit-preset-source-tag">
                    {t('autoProcess:shadowEditor.sourcePreset', { name: activePreset.name })}
                    {isAdjustedFromPreset && <span>{t('autoProcess:shadowEditor.adjusted')}</span>}
                  </div>
                )}
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={openSaveAsPresetDialog}
                  className="shdedit-round-btn"
                  data-testid="shdedit-save-as-preset-btn"
                  disabled={!imgLoaded}
                  block
                >
                  {t('autoProcess:shadowEditor.saveAsNewPreset')}
                </Button>
              </ShdEditGroup>

              <ShdEditGroup title={t('autoProcess:shadowEditor.lightPosition')}>
                <div className="shdedit-dial-row">
                  <div
                    ref={dialRef}
                    className="shdedit-dial"
                    data-testid="shdedit-dial"
                    onPointerDown={onDialPointerDown}
                    onPointerMove={onDialPointerMove}
                    onPointerUp={onDialPointerUp}
                    onPointerCancel={onDialPointerUp}
                  >
                    <Tooltip title={t('autoProcess:shadowEditor.dialCenterHint')}>
                      <div className="shdedit-dial-center" data-testid="shdedit-dial-center" />
                    </Tooltip>
                    {DIAL_AZIMUTHS.map((azimuth) => {
                      const pos = dialPos(azimuth, DIAL_RADIUS)
                      const active = Math.round(shadow.light.azimuthDeg) === azimuth
                      const label = t(`autoProcess:shadowEditor.dial.${DIAL_LABEL_KEY[azimuth]}`)
                      return (
                        <Tooltip key={azimuth} title={label}>
                          <button
                            type="button"
                            data-testid={`shdedit-dial-point-${azimuth}`}
                            className={`shdedit-dial-point${active ? ' active' : ''}`}
                            style={{ left: `calc(50% + ${pos.x}px)`, top: `calc(50% + ${pos.y}px)` }}
                            onClick={() => handleAzimuthOnly(azimuth)}
                            aria-label={label}
                          />
                        </Tooltip>
                      )
                    })}
                    <div
                      className="shdedit-dial-handle"
                      data-testid="shdedit-dial-handle"
                      style={{ left: `calc(50% + ${handlePos.x}px)`, top: `calc(50% + ${handlePos.y}px)` }}
                    />
                  </div>
                  <div className="shdedit-dial-side">
                    <div className="shdedit-hint">
                      {t('autoProcess:shadowEditor.lightDirectionHint')}
                    </div>
                    <ParamSlider
                      label={t('autoProcess:shadowEditor.lightHeight')}
                      testId="shdedit-height"
                      value={shadow.light.heightPct}
                      onChange={handleHeightOnly}
                    />
                  </div>
                </div>
              </ShdEditGroup>

              <ShdEditGroup title={t('autoProcess:shadowEditor.shadowAdjust')}>
                <ParamSlider
                  label={t('autoProcess:shadowEditor.shadowLength')}
                  testId="shdedit-length"
                  value={shadow.shadow.length}
                  onChange={(v) => setField({ length: v })}
                />
                <ParamSlider
                  label={t('autoProcess:shadowEditor.shadowSoftness')}
                  testId="shdedit-softness"
                  value={shadow.shadow.softness}
                  onChange={(v) => setField({ softness: v })}
                />
              </ShdEditGroup>

              <section className="shdedit-group">
                <button
                  type="button"
                  className="shdedit-advanced-toggle"
                  data-testid="shdedit-advanced-toggle"
                  onClick={() => setAdvancedOpen((v) => !v)}
                >
                  <span>{t('autoProcess:shadowEditor.advanced')}</span>
                  <span className="shdedit-advanced-caret">
                    {advancedOpen ? t('autoProcess:shadowEditor.collapse') : t('autoProcess:shadowEditor.expand')}
                  </span>
                </button>
                {advancedOpen && (
                  <div className="shdedit-advanced-body" data-testid="shdedit-advanced-body">
                    <ParamSlider
                      label={t('autoProcess:shadowEditor.shadowIntensity')}
                      testId="shdedit-strength"
                      value={shadow.shadow.strength}
                      onChange={(v) => setField({ strength: v })}
                    />
                    <ParamSlider
                      label={t('autoProcess:shadowEditor.farFade')}
                      testId="shdedit-far-fade"
                      value={shadow.shadow.farFade}
                      onChange={(v) => setField({ farFade: v })}
                    />
                    <ParamSlider
                      label={t('autoProcess:shadowEditor.projectionNarrow')}
                      testId="shdedit-taper"
                      value={shadow.shadow.taper}
                      onChange={(v) => setField({ taper: v })}
                    />
                    <div className="shdedit-slider-row" data-testid="shdedit-baseline-row">
                      <div className="shdedit-slider-head">
                        <span>{t('autoProcess:shadowEditor.groundBaseline')}</span>
                        <span>{Math.round(shadow.groundBaselineOffsetFrac * 100)}%</span>
                      </div>
                      <Slider
                        min={-5}
                        max={5}
                        step={0.5}
                        value={shadow.groundBaselineOffsetFrac * 100}
                        onChange={(v) => updateShadow((s) => ({ ...s, groundBaselineOffsetFrac: v / 100 }))}
                      />
                    </div>
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={handleReRecognizeContact}
                      className="shdedit-round-btn"
                      data-testid="shdedit-reidentify-btn"
                      block
                    >
                      {t('autoProcess:shadowEditor.redetectGround')}
                    </Button>
                  </div>
                )}
              </section>
            </div>
          </aside>

          <div className="shdedit-preview">
            <div className="shdedit-canvas-stage">
              <canvas ref={canvasRef} className="shdedit-canvas" data-testid="shdedit-canvas" />
              {!imgLoaded && !loadError && (
                <div className="shdedit-loading" data-testid="shdedit-loading">
                  {t('autoProcess:shadowEditor.loadingTransparent')}
                </div>
              )}
              {loadError && (
                <div className="shdedit-loading shdedit-loading-error" data-testid="shdedit-load-error">
                  {loadError}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={saveAsPresetOpen}
        onCancel={() => setSaveAsPresetOpen(false)}
        footer={null}
        closable={false}
        width={360}
        destroyOnClose
      >
        <div className="shdedit-save-name-dialog" data-testid="shdedit-save-as-preset-modal">
          <div className="shdedit-save-name-title">{t('autoProcess:shadowEditor.saveNewPresetTitle')}</div>
          <div className="shdedit-hint">
            {t('autoProcess:shadowEditor.saveNewPresetHint')}
          </div>
          <div className="shdedit-save-name-label">{t('autoProcess:shadowEditor.presetName')}</div>
          <Input
            autoFocus
            value={saveAsPresetName}
            onChange={(e) => setSaveAsPresetName(e.target.value)}
            onPressEnter={handleSaveAsNewPreset}
            data-testid="shdedit-save-as-preset-input"
            maxLength={40}
          />
          <div className="shdedit-save-name-actions">
            <Button onClick={() => setSaveAsPresetOpen(false)} data-testid="shdedit-save-as-preset-cancel">
              {t('actions:cancel')}
            </Button>
            <Button type="primary" onClick={handleSaveAsNewPreset} data-testid="shdedit-save-as-preset-confirm">
              {t('autoProcess:shadowEditor.createPreset')}
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  )
}

function ShdEditGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="shdedit-group">
      <div className="shdedit-group-title">{title}</div>
      {children}
    </section>
  )
}

function ParamSlider({
  label,
  testId,
  value,
  onChange,
  min = 0,
  max = 100,
}: {
  label: string
  testId: string
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
}) {
  return (
    <div className="shdedit-slider-row" data-testid={`${testId}-row`}>
      <div className="shdedit-slider-head">
        <span>{label}</span>
        <span data-testid={`${testId}-value`}>{Math.round(value)}</span>
      </div>
      <Slider min={min} max={max} value={clamp(value, min, max)} onChange={onChange} />
    </div>
  )
}
